import katex from 'katex';

/**
 * Call the /napi/formula/generate API to convert a natural-language
 * math description into a KaTeX string via Gemini Flash.
 */
export async function generateKatex(description: string): Promise<string> {
  const response = await fetch('/napi/formula/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ description }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error || `Formula API returned ${response.status}`);
  }

  const data = (await response.json()) as { katex: string };
  return data.katex;
}

/**
 * Validate that a KaTeX string renders without errors.
 * Handles:
 * - Pure KaTeX wrapped in $…$ or $$…$$
 * - Mixed content: plain text with $…$ or $$…$$ math parts
 */
export function validateKatex(latex: string): boolean {
  // Check for mixed content (plain text with $…$ parts)
  if (hasMixedContent(latex)) {
    return validateMixedContent(latex);
  }

  try {
    // Strip $…$ / $$…$$ wrappers — KaTeX.renderToString expects raw LaTeX
    const raw = latex
      .replace(/^\$\$/, '')
      .replace(/\$\$$/, '')
      .replace(/^\$/, '')
      .replace(/\$$/, '')
      .trim();
    if (!raw) return false;
    katex.renderToString(raw, { throwOnError: true });
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if a string is mixed content (has plain text outside of $ delimiters).
 */
function hasMixedContent(text: string): boolean {
  // If the whole string is wrapped in $$ or $, it's pure math
  if (/^\$\$[\s\S]+\$\$$/.test(text.trim())) return false;
  if (/^\$[^$]+\$$/.test(text.trim())) return false;
  // If it contains $ but also has text outside, it's mixed
  const stripped = text.replace(/\$\$[^$]+\$\$/g, '').replace(/\$[^$]+\$/g, '');
  return text.includes('$') && stripped.trim().length > 0;
}

/**
 * Validate mixed content by checking each $…$ / $$…$$ math part individually.
 */
function validateMixedContent(text: string): boolean {
  const parts: string[] = [];
  // Extract $$…$$ first, then $…$
  text.replace(/\$\$([^$]+)\$\$/g, (_, tex) => { parts.push(tex); return ''; });
  text.replace(/\$([^$]+)\$/g, (_, tex) => { parts.push(tex); return ''; });
  // If no math parts found, it's plain text — still valid
  if (parts.length === 0) return true;
  // Every math part must render
  return parts.every((tex) => {
    try {
      katex.renderToString(tex.trim(), { throwOnError: true });
      return true;
    } catch {
      return false;
    }
  });
}
